// The import on Home: after "close, it carries on in the background" the work stays visible. A quiet strip in the
// studio's work register, under the arrange banner: importing (with how much is in), stopped (with why), or in.
// It follows the newest confirmed plan with recent activity and goes away a day after it is in.
import { useEffect, useState } from "react";
import { ApiError } from "../../api.ts";
import { importsApi, type Plan, type RunView } from "../../data/imports-api.ts";
import { formatRoute } from "../../route.ts";
import { useApp } from "../app.ts";
import { Icon } from "../icons.tsx";
import { LinkButton } from "../kit.tsx";
import { classifyImportProgress, failReason, readerFor, runState } from "./ImportProgress.tsx";

/** While an import runs, and otherwise (a plan may be confirmed or read again from any screen at any time). */
const POLL_MS = 10_000;
const IDLE_MS = 30_000;
const STOPPED_SHOWN_MS = 3 * 24 * 3600_000;
const IN_SHOWN_MS = 24 * 3600_000;

export interface HomeImport {
  planId: string;
  state: "importing" | "stopped" | "in";
  title: string;
  detail: string;
}

const lastActivity = (views: RunView[]): number =>
  Math.max(
    0,
    ...views.map((v) => Date.parse(v.run.finished_at ?? v.run.created_at) || 0),
    ...views.map((v) => Date.parse(v.run.created_at) || 0),
  );

const projects = (n: number) => `${n} Higgsfield ${n === 1 ? "project" : "projects"}`;

/** What Home says about one plan's import, or null when there is nothing worth showing any more. */
export function homeImport(plan: Plan, views: RunView[], now: number): HomeImport | null {
  if (views.length === 0) return null;
  const reader = readerFor(
    plan,
    views.map((v) => v.run),
  );
  const p = classifyImportProgress(views, reader);
  const age = now - lastActivity(views);
  const n = views.length;
  if (!p.settled) {
    let done = 0;
    let all = 0;
    for (const v of views)
      for (const c of Object.values(v.counts)) {
        done += c.done + c.skipped + c.failed;
        all += c.queued + c.done + c.skipped + c.failed;
      }
    const waiting = views.map((v) => runState(v, reader)).find((s) => !s.terminal);
    return {
      planId: plan.id,
      state: "importing",
      title: `Importing ${projects(n)}`,
      detail: all > 0 ? `${done} of ${all} in` : (waiting?.label ?? "Starting"),
    };
  }
  if (p.failed > 0) {
    if (age > STOPPED_SHOWN_MS) return null;
    const stopped = views.find((v) => v.run.status === "failed");
    return {
      planId: plan.id,
      state: "stopped",
      title: p.completed > 0 ? `Import stopped for ${p.failed} of ${n} projects` : "The import stopped",
      detail: failReason(stopped?.run.error ?? null, reader),
    };
  }
  if (age > IN_SHOWN_MS) return null;
  return {
    planId: plan.id,
    state: "in",
    title: `${projects(n)} imported`,
    detail: "Open them from Productions.",
  };
}

/**
 * The import Home shows: the confirmed plan with the newest run activity. The list says which that is, so only that
 * plan's runs are read, and none once it is settled and too old to show (this runs every 30 s on an open Home).
 */
export async function loadHomeImport(
  im: Pick<ReturnType<typeof importsApi>, "plans" | "plan" | "run">,
  now: number,
): Promise<HomeImport | null> {
  const newest = (await im.plans("confirmed"))
    .filter((s) => s.last_run_at)
    .sort((a, b) => Date.parse(b.last_run_at as string) - Date.parse(a.last_run_at as string))[0];
  if (!newest) return null;
  if (newest.open_runs === 0 && now - Date.parse(newest.last_run_at as string) > STOPPED_SHOWN_MS)
    return null;
  const plan = await im.plan(newest.id);
  const views = await Promise.all(plan.items.flatMap((i) => (i.run_id ? [im.run(i.run_id)] : [])));
  return homeImport(plan, views, now);
}

/** A server without imports, or a role that cannot see them: Home shows nothing and stops asking. */
const permanent = (e: unknown) => e instanceof ApiError && [403, 404, 405, 501].includes(e.status);

type Timers = { set: (f: () => void, ms: number) => unknown; clear: (t: unknown) => void };

/**
 * Keep Home's import current: every 10 s while it runs and every 30 s otherwise, never giving up on a transient
 * failure (the last strip stays). `poke` asks again now (back on Home, the tab in view). Stops only on a permanent
 * refusal, or when the returned stop is called.
 */
export function watchHomeImport(
  load: () => Promise<HomeImport | null>,
  show: (shown: HomeImport | null) => void,
  timers: Timers = {
    set: (f, ms) => setTimeout(f, ms),
    clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  },
): { stop: () => void; poke: () => void } {
  let stopped = false;
  let timer: unknown;
  let round = 0;
  const step = async () => {
    if (timer !== undefined) timers.clear(timer);
    timer = undefined;
    const mine = ++round;
    let delay = IDLE_MS;
    try {
      const next = await load();
      if (stopped || mine !== round) return;
      show(next);
      if (next?.state === "importing") delay = POLL_MS;
    } catch (e) {
      if (stopped || mine !== round) return;
      if (permanent(e)) {
        show(null);
        stopped = true;
        return;
      }
    }
    timer = timers.set(() => void step(), delay);
  };
  void step();
  return {
    stop: () => {
      stopped = true;
      if (timer !== undefined) timers.clear(timer);
    },
    poke: () => {
      if (!stopped) void step();
    },
  };
}

/** "Importing 5 Higgsfield projects · 120 of 300 in · Open": kept current while Home is open. */
export function ImportStatus() {
  const { api, ws } = useApp();
  const [shown, setShown] = useState<HomeImport | null>(null);
  useEffect(() => {
    setShown(null);
    const im = importsApi(api, ws);
    const watch = watchHomeImport(() => loadHomeImport(im, Date.now()), setShown);
    // closing the import dialog (or any route change back to Home) and coming back to the tab ask again at once
    const onVisible = () => document.visibilityState === "visible" && watch.poke();
    window.addEventListener("hashchange", watch.poke);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      watch.stop();
      window.removeEventListener("hashchange", watch.poke);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, ws]);
  if (!shown) return null;
  return (
    <aside className={`imp-banner imp-status ${shown.state}`} aria-label="Import" aria-live="polite">
      <span className="imp-banner-mark" aria-hidden="true">
        <Icon
          name={shown.state === "stopped" ? "info" : shown.state === "in" ? "check" : "upload"}
          size={14}
        />
      </span>
      <p className="imp-banner-text">
        <strong>{shown.title}</strong>
        <span>{shown.detail}</span>
      </p>
      <LinkButton href={formatRoute({ view: "import", planId: shown.planId })} size="sm">
        Open
      </LinkButton>
    </aside>
  );
}
